import styles from './Steps.module.scss';
import Heading from '@ui/components/Heading';
import Content from '@ui/components/Content';
import Button from '@ui/components/Button';
import Link from '@ui/components/Link';
import FontAwesomeProIcon from '@ui/components/FontAwesomeProIcon';
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { getHeadingTag } from '../../utils';

const StepItemIcon = ({
  icon,
  headingLevel = 3,
  title,
  content,
  link,
  noLine = false,
}) => (
  <li
    className={classNames(styles.step, styles.iconStep, {
      [styles.noLine]: noLine,
    })}
  >
    <span
      className={classNames(styles.stepIcon, {
        [styles.noLine]: noLine,
      })}
    >
      <FontAwesomeProIcon icon={icon} role="presentation" />
    </span>
    <div className={styles.content}>
      <Heading
        as={getHeadingTag(headingLevel)}
        className={styles.heading}
        noSpacing
        size="h5"
      >
        {title}
      </Heading>
      {!!content && (
        <Content className={styles.text} size="md" withParser>
          {content}
        </Content>
      )}
      {!!link?.url && (
        <Button
          render={<Link target={link?.target} to={link?.url} />}
          variant="link"
        >
          {link?.title}
        </Button>
      )}
    </div>
  </li>
);

StepItemIcon.propTypes = {
  content: PropTypes.string,
  headingLevel: PropTypes.number,
  icon: PropTypes.string.isRequired,
  link: PropTypes.shape({
    title: PropTypes.string,
    url: PropTypes.string,
    target: PropTypes.string,
  }),
  noLine: PropTypes.bool,
  title: PropTypes.string,
};

const StepItem = ({
  headingLevel = 3,
  content,
  index,
  link,
  noLine = false,
  title,
}) => (
  <li
    className={classNames(styles.step, {
      [styles.noLine]: noLine,
    })}
  >
    <span
      className={classNames(styles.count, {
        [styles.noLine]: noLine,
      })}
    >
      {index}
    </span>
    <div className={styles.content}>
      {!!title && (
        <Heading
          as={getHeadingTag(headingLevel)}
          className={styles.heading}
          noSpacing
          size="h5"
        >
          {title}
        </Heading>
      )}
      {!!content && (
        <Content className={styles.text} size="md" withParser>
          {content}
        </Content>
      )}
      {!!link?.url && (
        <Button
          render={<Link target={link?.target} to={link?.url} />}
          variant="link"
        >
          {link?.title}
        </Button>
      )}
    </div>
  </li>
);

StepItem.propTypes = {
  content: PropTypes.string,
  headingLevel: PropTypes.number,
  index: PropTypes.string.isRequired,
  link: PropTypes.shape({
    title: PropTypes.string,
    url: PropTypes.string,
    target: PropTypes.string,
  }),
  noLine: PropTypes.bool,
  title: PropTypes.string,
};

const Steps = ({
  headingLevel = 3,
  className = null,
  items,
  noLine = false,
  variant = 'default',
}) => (
  <ol className={classNames(styles.container, className)}>
    {variant === 'default'
      ? items?.map(({ id, index, title, content, link }) => (
          <StepItem
            content={content}
            headingLevel={headingLevel}
            index={index}
            key={id}
            link={link}
            noLine={noLine}
            title={title}
          />
        ))
      : items?.map(({ id, icon, title, content, link }, index) => (
          <StepItemIcon
            content={content}
            headingLevel={headingLevel}
            icon={icon}
            index={index}
            key={id}
            link={link}
            noLine={noLine}
            title={title}
          />
        ))}
  </ol>
);

Steps.propTypes = {
  className: PropTypes.string,
  headingLevel: PropTypes.number,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      index: PropTypes.string,
      title: PropTypes.string,
      content: PropTypes.string,
      icon: PropTypes.string,
      link: PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
        target: PropTypes.string,
      }),
    }),
  ),
  noLine: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'icon']),
};

export default Steps;
